// Automatizaciones creadas en la Consola (D35): "Nueva automatización" y sus tarjetas. Por ahora el tipo disponible es
// el aviso de licencias 3CX a Partners; las respuestas por grupo y las reglas llegan despues.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { correosValidos, EJEMPLO, normalizarCorreos, rellenar } from '../../api/automatizaciones';
import { type AutoCreada, automatizacionesCreadas, crearAutomatizacion, guardarCreada, NUEVA_LICENCIAS } from '../../api/automatizacionesCreadas';
import { enviar } from '../../api/correo';
import { useSession } from '../../auth/session';
import { firmaDe } from '../../lib/correo';
import { cuerpoHtml, EJEMPLO_LICENCIAS, tablaHtml } from '../../lib/correoLicencias';
import { Icon } from '../../lib/icons';
import { Pill } from '../../ui/bits';
import { Dialog } from '../../ui/Dialog';
import { usePlantillas } from '../../ui/Plantillas';
import { toast } from '../../ui/toasts';
import { useBuzonesGrupo } from './BuzonesGrupo';

export const useCreadas = () => useQuery({ queryKey: ['automatizaciones-creadas'], queryFn: automatizacionesCreadas, staleTime: 60_000 });

const horaTexto = (h: number) => (h === 0 ? '12:00 a. m.' : h < 12 ? `${h}:00 a. m.` : h === 12 ? '12:00 p. m.' : `${h - 12}:00 p. m.`);

// Boton y dialogo para elegir el tipo de automatizacion nueva.
export function NuevaAutomatizacion() {
  const qc = useQueryClient();
  const [abierto, setAbierto] = useState(false);
  const [creando, setCreando] = useState(false);
  const crear = async () => {
    setCreando(true);
    try {
      await crearAutomatizacion(NUEVA_LICENCIAS);
      await qc.invalidateQueries({ queryKey: ['automatizaciones-creadas'] });
      toast('Automatización creada (apagada). Ajústala y actívala abajo.');
      setAbierto(false);
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setCreando(false);
    }
  };
  return (
    <>
      <button className="btn primary" onClick={() => setAbierto(true)}>
        <Icon name="plus" /> Nueva automatización
      </button>
      {abierto ? (
        <Dialog titulo="Nueva automatización" subtitulo="Elige qué tipo quieres crear. Nace apagada: no envía nada hasta que la actives." onClose={() => setAbierto(false)} width={560}>
          <div className="rows">
            <button className="row act-row" onClick={() => void crear()} disabled={creando}>
              <span className="ico-dot">
                <Icon name="key" />
              </span>
              <span style={{ minWidth: 0 }}>
                <span className="nm" style={{ display: 'block' }}>
                  Aviso de licencias 3CX a Partners
                </span>
                <span className="sb" style={{ display: 'block' }}>
                  Un correo a cada Partner cuando a sus licencias les faltan los días que elijas (p. ej. 60 o 10), con su buzón, texto, idioma y copias.
                </span>
              </span>
            </button>
            <div className="row" style={{ opacity: 0.55 }}>
              <span className="ico-dot">
                <Icon name="inbox" />
              </span>
              <span>
                <span className="nm" style={{ display: 'block' }}>Respuesta automática por grupo</span>
                <span className="sb" style={{ display: 'block' }}>Pronto: acuse de caso nuevo o resuelto distinto para cada grupo.</span>
              </span>
            </div>
            <div className="row" style={{ opacity: 0.55 }}>
              <span className="ico-dot">
                <Icon name="sliders" />
              </span>
              <span>
                <span className="nm" style={{ display: 'block' }}>Regla "si pasa esto, haz esto"</span>
                <span className="sb" style={{ display: 'block' }}>Pronto: como los triggers de Zammad (asignar, cambiar grupo, etiquetar o enviar un correo).</span>
              </span>
            </div>
          </div>
        </Dialog>
      ) : null}
    </>
  );
}

export function TarjetaCreada({ guardada }: { guardada: AutoCreada }) {
  const qc = useQueryClient();
  const { yo } = useSession();
  const grupos = useBuzonesGrupo();
  const plantillas = usePlantillas();
  const [v, setV] = useState(guardada);
  const [abierta, setAbierta] = useState(false);
  const [entendido, setEntendido] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);
  const [paraPrueba, setParaPrueba] = useState('');
  const [probando, setProbando] = useState(false);
  useEffect(() => setV(guardada), [guardada]);
  const id = (x: string) => `auto-${guardada.id}-${x}`;

  const compartidos = (grupos.data ?? []).filter((g) => g.cuentaId && g.activo);
  const buzonElegido = compartidos.find((g) => g.correo.toLowerCase() === v.buzon.toLowerCase());
  const firma = buzonElegido?.firma || firmaDe(v.buzon, (plantillas.data ?? []).filter((x) => x.tipo === 'FIRMA' && !x.duenoId), '');
  const datos = { ...EJEMPLO, dias: String(v.dias) };
  const html = cuerpoHtml(v.cuerpo, datos, tablaHtml(EJEMPLO_LICENCIAS, v.idioma === 'EN' ? 'EN' : 'ES'), firma);
  const activando = v.activa && !guardada.activa;
  const diasOk = Number.isInteger(v.dias) && v.dias >= 0 && v.dias <= 90;
  const copiasOk = correosValidos(v.copias);
  const puedeGuardar = !!v.buzon && !!v.cuerpo.trim() && diasOk && copiasOk && (!activando || entendido);
  const refrescar = () => qc.invalidateQueries({ queryKey: ['automatizaciones-creadas'] });

  const guardar = async () => {
    setGuardando(true);
    try {
      await guardarCreada({ ...v, copias: normalizarCorreos(v.copias) });
      await refrescar();
      setEntendido(false);
      setAbierta(false);
      toast(v.activa ? `${v.nombre}: activa.` : `${v.nombre}: guardada (apagada).`);
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setGuardando(false);
    }
  };
  const borrar = async () => {
    if (!confirmarBorrar) return setConfirmarBorrar(true);
    try {
      await guardarCreada({ ...guardada, oculta: true, activa: false });
      await refrescar();
      toast(`${guardada.nombre}: borrada. La puedes restaurar al final de la lista.`);
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  };
  const enviarPrueba = async () => {
    const para = (paraPrueba || yo.email || '').trim();
    if (!buzonElegido?.cuentaId || !para) return;
    setProbando(true);
    try {
      await enviar({ cuentaId: buzonElegido.cuentaId, para: [para], asunto: `[Prueba] ${rellenar(v.asunto, datos)}`, texto: rellenar(v.cuerpo, datos), html });
      toast(`Prueba enviada a ${para} desde ${buzonElegido.correo}.`);
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setProbando(false);
    }
  };

  return (
    <section className="box" style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 16 }}>{guardada.nombre}</h2>
        <Pill tone={guardada.activa ? 'ok' : undefined}>{guardada.activa ? 'Activo' : 'Apagado'}</Pill>
        <Pill>Creada</Pill>
        <button className="btn sm ghost" style={{ marginLeft: 'auto' }} onClick={() => setAbierta(!abierta)}>
          {abierta ? 'Cerrar' : 'Configurar'}
        </button>
      </div>
      <p className="note" style={{ margin: 0 }}>
        Cada Partner {guardada.idioma ? `con idioma ${guardada.idioma === 'ES' ? 'español' : 'inglés'} ` : ''}recibe un correo cuando a sus licencias 3CX les faltan {guardada.dias} días o menos (una sola vez por licencia), desde {guardada.buzon || '—'}
        {guardada.copias ? `, con copia a ${guardada.copias}` : ''}.
      </p>
      {abierta ? (
        <div style={{ display: 'grid', gap: 10 }}>
          <label>
            Nombre
            <input id={id('nombre')} value={v.nombre} onChange={(e) => setV({ ...v, nombre: e.target.value })} />
          </label>
          <div className="programacion">
            <label>
              Avisar
              <span className="con-sufijo">
                <input id={id('dias')} type="number" min={0} max={90} value={v.dias} onChange={(e) => setV({ ...v, dias: Number(e.target.value) })} />
                días antes
              </span>
            </label>
            <label>
              Partners
              <select id={id('idioma')} value={v.idioma} onChange={(e) => setV({ ...v, idioma: e.target.value as AutoCreada['idioma'] })}>
                <option value="">Todos</option>
                <option value="ES">Solo idioma español</option>
                <option value="EN">Solo idioma inglés</option>
              </select>
            </label>
            <label>
              Hora de envío
              <select id={id('hora')} value={v.hora ?? 7} onChange={(e) => setV({ ...v, hora: Number(e.target.value) })}>
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>
                    {horaTexto(h)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Enviar desde
              <select id={id('buzon')} value={v.buzon} onChange={(e) => setV({ ...v, buzon: e.target.value })}>
                <option value="">Elige un buzón de grupo</option>
                {compartidos.map((g) => (
                  <option key={g.id} value={g.correo.toLowerCase()}>
                    {g.nombre} · {g.correo}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ gridColumn: '1 / -1' }}>
              Con copia a
              <input id={id('copias')} value={v.copias} onChange={(e) => setV({ ...v, copias: e.target.value })} placeholder="kmora@xmartek.com, otro@xmartek.com" />
            </label>
            <p className="note" style={{ margin: 0, gridColumn: '1 / -1' }}>
              {!diasOk ? 'Los días van de 0 a 90.' : !copiasOk ? 'Revisa las copias: correos completos separados por coma.' : 'Sale en tandas desde esa hora hasta las 6 p. m. (Colombia), junto con los avisos fijos. Es independiente de los avisos de 45 y 25 días.'}
            </p>
          </div>
          <label>
            Asunto
            <input id={id('asunto')} value={v.asunto} onChange={(e) => setV({ ...v, asunto: e.target.value })} />
          </label>
          <label>
            Texto
            <textarea id={id('cuerpo')} rows={8} value={v.cuerpo} onChange={(e) => setV({ ...v, cuerpo: e.target.value })} />
          </label>
          <p className="note" style={{ margin: 0 }}>
            Puedes usar: {'{{contacto.nombre}}, {{empresa.nombre}}, {{dias}}, {{licencias.tabla}}'}. Para negrita escribe **así**.
          </p>
          <div className="vista-previa">
            <span className="lbl">Así lo verá el Partner</span>
            <b>{rellenar(v.asunto, datos)}</b>
            <div className="correo-html" dangerouslySetInnerHTML={{ __html: html }} />
          </div>
          <label className="opcion">
            <input id={id('activa')} type="checkbox" checked={v.activa} onChange={(e) => setV({ ...v, activa: e.target.checked })} /> Enviar automáticamente
          </label>
          {activando ? (
            <label className="opcion aviso">
              <input id={id('entendido')} type="checkbox" checked={entendido} onChange={(e) => setEntendido(e.target.checked)} /> Entiendo que desde ahora saldrán correos reales a los Partners.
            </label>
          ) : null}
          <div className="prueba">
            <span>Enviar prueba a</span>
            <input id={id('prueba')} value={paraPrueba} onChange={(e) => setParaPrueba(e.target.value)} placeholder={yo.email ?? 'tu@correo.com'} />
            <button type="button" className="btn sm" onClick={() => void enviarPrueba()} disabled={probando || !buzonElegido?.cuentaId}>
              {probando ? 'Enviando…' : 'Enviar prueba'}
            </button>
            {!buzonElegido ? <span className="note">Elige primero el buzón que envía.</span> : null}
          </div>
          <div className="actions">
            <button type="button" className={`btn ${confirmarBorrar ? 'danger' : 'ghost'}`} onClick={() => void borrar()} style={{ marginRight: 'auto' }}>
              {confirmarBorrar ? 'Sí, borrar (deja de enviar)' : 'Borrar automatización'}
            </button>
            <button className="btn primary" onClick={() => void guardar()} disabled={!puedeGuardar || guardando}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

// Restaurar una creada que se borro.
export function RestaurarCreada({ a }: { a: AutoCreada }) {
  const qc = useQueryClient();
  const restaurar = async () => {
    try {
      await guardarCreada({ ...a, oculta: false, activa: false });
      await qc.invalidateQueries({ queryKey: ['automatizaciones-creadas'] });
      toast(`${a.nombre}: restaurada (apagada).`);
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ flex: 1 }}>{a.nombre}</span>
      <button className="btn sm" onClick={() => void restaurar()}>
        Restaurar
      </button>
    </div>
  );
}
